import React, { useEffect, useState, useCallback } from 'react';
import { agentFetch } from '../../utils/agentFetch';

export interface DriveNode {
  id: string;
  name: string;
  type: 'folder' | 'file';
  children?: DriveNode[];
}

export interface Session {
  id: string;
  title: string;
  last_updated?: string;
}

interface Props {
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onSessionRestore: (sessionId: string) => void;
  sessions: Session[];
  onSessionsReload: () => void;
}

function collectFolderIds(nodes: DriveNode[]): string[] {
  const ids: string[] = [];
  for (const n of nodes) {
    if (n.type === 'folder') {
      ids.push(n.id);
      if (n.children) ids.push(...collectFolderIds(n.children));
    }
  }
  return ids;
}

function countSelectedLeaves(nodes: DriveNode[], selectedIds: Set<string>): number {
  let count = 0;
  for (const n of nodes) {
    if (n.type !== 'folder') continue;
    const folderChildren = n.children?.filter(c => c.type === 'folder') || [];
    if (folderChildren.length === 0) {
      if (selectedIds.has(n.id)) count++;
    } else {
      count += countSelectedLeaves(folderChildren, selectedIds);
    }
  }
  return count;
}

function getFolderIcon(name: string, level: number): { icon: string; title: string } {
  if (level > 0) return { icon: '▸', title: '' };
  const lower = name.toLowerCase();
  if (lower === 'rdf' || lower.startsWith('rdf ') || lower.startsWith('rdf-')) return { icon: '◈', title: 'RDF' };
  return { icon: '◉', title: '' };
}

function getDisplayName(name: string): string {
  if (name.toLowerCase().includes('regulatory text')) return 'Regulation';
  return name;
}

function sortNodes(nodes: DriveNode[]): DriveNode[] {
  return [...nodes].sort((a, b) => {
    const aIsRdf = a.name.toLowerCase().includes('rdf');
    const bIsRdf = b.name.toLowerCase().includes('rdf');
    if (aIsRdf && !bIsRdf) return -1;
    if (!aIsRdf && bIsRdf) return 1;
    return a.name.localeCompare(b.name);
  });
}

function FolderNode({
  node,
  level,
  selectedIds,
  onToggle,
}: {
  node: DriveNode;
  level: number;
  selectedIds: Set<string>;
  onToggle: (node: DriveNode, checked: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const folderChildren = node.children?.filter(c => c.type === 'folder') || [];
  const hasChildren = folderChildren.length > 0;
  const checked = selectedIds.has(node.id);

  return (
    <li className="ai-tree-item">
      <div className="ai-tree-row" style={{ paddingLeft: level * 8 }}>
        <span
          className={`ai-tree-chevron${hasChildren ? '' : ' invisible'}`}
          onClick={() => hasChildren && setOpen(o => !o)}
          style={{ transform: open ? 'rotate(90deg)' : undefined }}
        >
          ▶
        </span>
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onToggle(node, e.target.checked)}
          className="ai-tree-checkbox"
        />
        <span
          className="ai-tree-label"
          style={{ fontWeight: level === 0 ? 600 : 400, fontSize: level === 0 ? '0.82rem' : '0.75rem' }}
          onClick={() => hasChildren && setOpen(o => !o)}
          title={node.name}
        >
          {(() => { const { icon, title } = getFolderIcon(node.name, level); return <span style={{ color: 'var(--c-primary)', marginRight: 4, flexShrink: 0 }} title={title}>{icon}</span>; })()}
          {getDisplayName(node.name)}
        </span>
      </div>
      {hasChildren && open && (
        <ul className="ai-tree-children">
          {folderChildren.map(child => (
            <FolderNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedIds={selectedIds}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export const DocumentSidebar: React.FC<Props> = ({
  selectedIds,
  onSelectionChange,
  onSessionRestore,
  sessions,
  onSessionsReload,
}) => {
  const [nodes, setNodes] = useState<DriveNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  useEffect(() => {
    const CACHE_KEY = 'rdf_drive_hierarchy';
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        setNodes(JSON.parse(cached));
        setLoading(false);
        return;
      } catch (_) {}
    }
    agentFetch('/api/drive-hierarchy')
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        setNodes(arr);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(arr));
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  const handleToggle = useCallback((node: DriveNode, checked: boolean) => {
    const ids = new Set(selectedIds);
    // Toggle this node + all descendants
    const toggleAll = (n: DriveNode) => {
      if (n.type === 'folder') {
        checked ? ids.add(n.id) : ids.delete(n.id);
        n.children?.forEach(toggleAll);
      }
    };
    toggleAll(node);
    onSelectionChange(ids);
  }, [selectedIds, onSelectionChange]);

  const handleToggleAll = () => {
    const allIds = collectFolderIds(nodes);
    if (selectedIds.size > 0) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(allIds));
    }
  };

  const commitRename = async (sessionId: string, newTitle: string) => {
    if (!newTitle.trim()) { setRenamingId(null); return; }
    try {
      await agentFetch(`/api/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle }),
      });
      onSessionsReload();
    } catch (_) {}
    setRenamingId(null);
  };

  return (
    <aside className="ai-sidebar">
      <div className="ai-sidebar-section">
        <div className="ai-sidebar-header">
          <h4>Knowledge Base</h4>
          <button className="ai-btn-small" onClick={handleToggleAll}>
            {selectedIds.size > 0 ? 'None' : 'All'}
          </button>
        </div>
        {loading && (
          <p className="ai-sidebar-hint"><i>Loading library…</i></p>
        )}
        {error && (
          <p style={{ color: 'var(--c-error)', fontSize: '0.82rem', padding: '8px 0' }}>
            Error loading library.
          </p>
        )}
        {!loading && !error && (
          <ul className="ai-tree">
            {sortNodes(nodes.filter(n => n.type === 'folder')).map(n => (
              <FolderNode
                key={n.id}
                node={n}
                level={0}
                selectedIds={selectedIds}
                onToggle={handleToggle}
              />
            ))}
          </ul>
        )}
        <p className="ai-source-capacity">{countSelectedLeaves(nodes, selectedIds)} items active</p>
      </div>

      <div className="ai-sidebar-section">
        <div className="ai-sidebar-header">
          <h4>Previous Sessions</h4>
        </div>
        <div className="ai-sessions-list">
          {sessions.length === 0 && (
            <p className="ai-sidebar-hint">No previous sessions.</p>
          )}
          {sessions.slice(0, 5).map(s => (
            <div key={s.id} className="ai-session-item" onClick={() => renamingId !== s.id && onSessionRestore(s.id)}>
              {renamingId === s.id ? (
                <input
                  className="ai-session-rename-input"
                  autoFocus
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onBlur={() => commitRename(s.id, renameValue)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitRename(s.id, renameValue);
                    if (e.key === 'Escape') setRenamingId(null);
                  }}
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <>
                  <span className="ai-session-title" title={s.title}>
                    {s.title.length > 50 ? s.title.slice(0, 50) + '…' : s.title}
                  </span>
                  <span className="ai-session-date">
                    {s.last_updated ? new Date(s.last_updated).toLocaleDateString() : ''}
                  </span>
                  <button
                    className="ai-session-rename-btn"
                    title="Rename"
                    onClick={e => {
                      e.stopPropagation();
                      setRenamingId(s.id);
                      setRenameValue(s.title);
                    }}
                  >
                    ✏
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
};
